import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { Resolver, useForm, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  createMenuConfigFormSchema,
  MenuModuleConfigSchema,
} from "../schemas/modules/menu";
import { defaultMenuModuleFormConfig } from "../defaults/modules/menu";
import {
  convertAgentBlueprintToFormValues,
  convertFormValuesToMenuConfig,
  convertMenuConfigToFormValues,
} from "../utils/form-flattening-utils";
import { PatchAgentFormSchema } from "@/schemas/forms/patch";
import { useAgentEditor } from "./AgentEditorContext";
import { MenuConfigFormValues, PatchAgentFormValues } from "@/types/forms";
import { defaultPatchAgentFormValues } from "@/defaults/basic";

interface AgentFormContextType {
  agentForm: UseFormReturn<PatchAgentFormValues>;
  menuForm: UseFormReturn<MenuConfigFormValues>;
  getNestedMenuConfig: () => z.infer<typeof MenuModuleConfigSchema> | undefined;
  hasUnsavedChanges: boolean;
}

const AgentFormContext = createContext<AgentFormContextType | null>(null);

export function AgentFormProvider({ children }: { children: ReactNode }) {
  const { localAgentBlueprint, selectedAgent } = useAgentEditor();

  const agentForm = useForm<PatchAgentFormValues>({
    resolver: zodResolver(
      PatchAgentFormSchema,
    ) as Resolver<PatchAgentFormValues>,
    defaultValues: defaultPatchAgentFormValues,
  });

  const menuForm = useForm<MenuConfigFormValues>({
    resolver: zodResolver(
      createMenuConfigFormSchema([], [], [], []),
    ) as unknown as Resolver<MenuConfigFormValues>,
    defaultValues: defaultMenuModuleFormConfig,
  });

  const getNestedMenuConfig = useMemo(() => {
    return () => {
      const menuConfigFormValues = menuForm.getValues();
      return convertFormValuesToMenuConfig(menuConfigFormValues);
    };
  }, [menuForm]);

  // Compute hasUnsavedChanges based on both forms' dirty states
  const hasUnsavedChanges = useMemo(() => {
    return agentForm.formState.isDirty || menuForm.formState.isDirty;
  }, [agentForm.formState.isDirty, menuForm.formState.isDirty]);

  // Load saved form data and user edited fields on mount or when agent changes
  useEffect(() => {
    if (!localAgentBlueprint?.id) return;
    try {
      // Convert nested agent blueprint to flattened structure
      const agentFormValues =
        convertAgentBlueprintToFormValues(localAgentBlueprint);
      agentForm.reset(agentFormValues);

      // Handle menu config with new flattened structure
      const menuConfig = localAgentBlueprint?.posProvider?.menuModuleConfig;
      if (menuConfig) {
        const menuConfigFormValues = convertMenuConfigToFormValues(menuConfig);
        menuForm.reset(menuConfigFormValues);
      }
    } catch (error) {
      console.error("Error loading form data from localStorage:", error);
    }
  }, [localAgentBlueprint, agentForm, menuForm, selectedAgent?.id]);

  const contextValue: AgentFormContextType = {
    agentForm,
    menuForm,
    getNestedMenuConfig,
    hasUnsavedChanges,
  };

  return (
    <AgentFormContext.Provider value={contextValue}>
      {children}
    </AgentFormContext.Provider>
  );
}

export function useAgentForm() {
  const context = useContext(AgentFormContext);
  if (!context) {
    throw new Error("useAgentForm must be used within an AgentFormProvider");
  }
  return context;
}
